import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, Trophy, Heart, PartyPopper, Ticket } from 'lucide-react';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';

interface LevelUpCelebrationProps {
  level: number;
  rewards: { type: string; amount: number | string }[];
  onClose: () => void;
}

export default function LevelUpCelebration({ level, rewards, onClose }: LevelUpCelebrationProps) {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="absolute inset-0 z-[2000] flex items-center justify-center pointer-events-none">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-dark/80 backdrop-blur-md pointer-events-auto"
          onClick={onClose}
        />
      </AnimatePresence>

      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", damping: 12, stiffness: 200 }}
        className="relative z-10 w-full max-w-sm bg-slate-900 rounded-[50px] p-8 cartoon-border cartoon-shadow m-4 pointer-events-auto overflow-hidden"
      >
        {/* Background Burst */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 opacity-10 pointer-events-none"
        >
           <Star className="w-full h-full text-yellow-500" />
        </motion.div>

        <div className="text-center relative z-10">
          <motion.h2
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="text-5xl font-num text-primary uppercase drop-shadow-[0_4px_0_rgba(0,0,0,0.1)] mb-2"
          >
            LEVEL UP!
          </motion.h2>

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.7, type: "spring" }}
            className="inline-block bg-slate-950 text-white font-num text-3xl px-6 py-2 rounded-full mb-8 border-2 border-slate-800"
          >
            LVL {level}
          </motion.div>

          <div className="space-y-4 mb-8 text-left">
            <h3 className="text-slate-400 font-black uppercase text-xs tracking-widest text-center mb-4">REWARDS UNLOCKED</h3>
            {rewards.map((reward, i) => (
              <motion.div
                key={i}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.9 + i * 0.1 }}
                className="flex items-center gap-4 bg-slate-800 p-4 rounded-2xl cartoon-border border-t-0 border-x-0 border-slate-950"
              >
                <div className="bg-slate-900 p-1 rounded-xl cartoon-border border-2 border-slate-700">
                  {reward.type === 'coins' && <CurrencyIcon type="coin" size="sm" />}
                  {reward.type === 'gems' && <CurrencyIcon type="gem" size="sm" />}
                  {reward.type === 'tickets' && <Star className="w-5 h-5 text-primary" />}
                  {reward.type === 'star_tickets' && <Ticket className="w-5 h-5 text-amber-400" />}
                  {reward.type === 'unlock' && <Trophy className="w-5 h-5 text-secondary" />}
                  {reward.type === 'badge' && <Star className="w-5 h-5 text-orange-500" />}
                  {reward.type === 'perk' && <Star className="w-5 h-5 text-green-500" />}
                </div>
                <div>
                  <div className="text-white font-black text-lg">
                    {reward.type === 'coins' ? `+${reward.amount} Coins` : 
                     reward.type === 'gems' ? `+${reward.amount} Hoshi` : 
                     reward.type === 'star_tickets' ? `+${reward.amount} Star Ticket${Number(reward.amount) > 1 ? 's' : ''}` :
                     reward.amount}
                  </div>
                  <div className="text-slate-500 text-[10px] font-black uppercase">
                    {reward.type === 'coins' ? 'COINS' : 
                     reward.type === 'gems' ? 'HOSHI' : 
                     reward.type === 'star_tickets' ? 'FOR STAR BURST' :
                     'BONUS REWARD'}
                  </div>
                </div>
              </motion.div>
            ))}
            
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.9 + rewards.length * 0.1 }}
              className="flex items-center gap-4 bg-slate-800 p-4 rounded-2xl cartoon-border border-t-0 border-x-0 border-slate-950"
            >
              <div className="bg-slate-900 p-2 rounded-xl cartoon-border border-2 border-slate-700">
                 <Heart className="w-5 h-5 text-primary fill-current" />
              </div>
              <div>
                <div className="text-white font-black text-lg">FULL REFILL</div>
                <div className="text-slate-500 text-[10px] font-black uppercase">HEARTS RESTORED</div>
              </div>
            </motion.div>
          </div>

          {[10, 15, 20, 25, 30].includes(level) && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 1.2 + rewards.length * 0.1, type: "spring" }}
              className="bg-amber-500/10 border-2 border-amber-500/30 p-4 rounded-2xl mb-6 text-center shadow-sm"
            >
              <div className="text-amber-400 font-black text-xs uppercase tracking-wider mb-1">SPECIAL OFFER UNLOCKED!</div>
              <div className="text-slate-300 text-[10px] font-bold leading-normal">A new one-time Milestone Offer is now available in the Shop's Market!</div>
            </motion.div>
          )}

          <button
            onClick={onClose}
            className="w-full bg-secondary text-white font-num text-3xl py-5 rounded-[24px] transition-all active:cartoon-shadow-active cartoon-border cartoon-shadow border-slate-900"
          >
            AWESOME!
          </button>
        </div>

        {/* Coin Rain Effect */}
        {showContent && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 30 }).map((_, i) => {
              const isGem = rewards.some(r => r.type === 'gems') && Math.random() > 0.7;
              return (
                <motion.div
                  key={i}
                  initial={{ y: -20, x: Math.random() * 400 - 50, opacity: 1 }}
                  animate={{ 
                    y: 600, 
                    rotate: 360 * 3,
                    x: (Math.random() - 0.5) * 200 + (Math.random() * 400 - 50)
                  }}
                  transition={{ 
                    duration: 2 + Math.random() * 2, 
                    repeat: Infinity,
                    delay: Math.random() * 2
                  }}
                  className="absolute"
                >
                  <CurrencyIcon type={isGem ? 'gem' : 'coin'} size="sm" />
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
